import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

// Maps hardcoded product numeric IDs to deterministic UUIDs in the DB
const PRODUCT_UUID_MAP: Record<number, string> = {
  1: "00000000-0000-0000-0000-000000000001",
  2: "00000000-0000-0000-0000-000000000002",
  3: "00000000-0000-0000-0000-000000000003",
  4: "00000000-0000-0000-0000-000000000004",
  5: "00000000-0000-0000-0000-000000000005",
  6: "00000000-0000-0000-0000-000000000006",
  7: "00000000-0000-0000-0000-000000000007",
  9: "00000000-0000-0000-0000-000000000009",
  10: "00000000-0000-0000-0000-00000000000a",
  11: "00000000-0000-0000-0000-00000000000b",
  12: "00000000-0000-0000-0000-00000000000c",
  14: "00000000-0000-0000-0000-00000000000e",
};

// Reverse map: UUID → numeric ID
const UUID_TO_ID: Record<string, number> = Object.fromEntries(
  Object.entries(PRODUCT_UUID_MAP).map(([k, v]) => [v, Number(k)])
);

export function getProductUUID(numericId: number): string | undefined {
  return PRODUCT_UUID_MAP[numericId];
}

interface CartProduct {
  id: number;
  qty: number;
  /** Optional variant UUID. When set, this line represents a specific variant
   *  (sabor/tamaño) and is deduplicated separately from the bare product. */
  variantId?: string | null;
  /** Display overrides applied when a variant is selected on the PDP. */
  variantName?: string;
  variantPrice?: number;
  variantImg?: string;
  variantStk?: number;
  /** Optional bundle UUID. When set, this line was added as part of a
   *  featured bundle — its `pr` is already the discounted unit price and
   *  `cpr` carries the original price for strike-through display. */
  bundleId?: string | null;
  bundleTitle?: string;
  [key: string]: any;
}

/**
 * Hook that syncs cart state with the cart_items DB table for authenticated users.
 * Falls back to local-only state for guests.
 */
export function useCart(userId: string | null, allProducts: any[]) {
  const [cart, setCart] = useState<CartProduct[]>([]);
  const [cartLoaded, setCartLoaded] = useState(false);
  const syncingRef = useRef(false);
  const initialLoadDone = useRef(false);

  // Load cart from DB on mount / user change
  useEffect(() => {
    if (!userId) {
      setCartLoaded(true);
      initialLoadDone.current = true;
      return;
    }

    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("cart_items")
        .select("product_id, quantity, variant_id, bundle_id")
        .eq("user_id", userId);

      if (cancelled || error) {
        setCartLoaded(true);
        initialLoadDone.current = true;
        return;
      }

      const items: CartProduct[] = [];
      for (const row of data || []) {
        const numId = UUID_TO_ID[row.product_id];
        if (numId == null) continue;
        const prod = allProducts.find((p: any) => p.id === numId);
        if (!prod) continue;
        items.push({
          ...prod,
          qty: row.quantity,
          variantId: (row as any).variant_id ?? null,
          bundleId: (row as any).bundle_id ?? null,
        });
      }
      setCart(items);
      setCartLoaded(true);
      initialLoadDone.current = true;
    })();

    return () => { cancelled = true; };
  }, [userId]); // allProducts is stable (hardcoded)

  // Sync cart to DB whenever it changes (debounced)
  const syncTimeout = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    if (!userId || !initialLoadDone.current) return;
    if (syncingRef.current) return;

    clearTimeout(syncTimeout.current);
    syncTimeout.current = setTimeout(async () => {
      syncingRef.current = true;
      try {
        // Delete existing cart items
        await supabase.from("cart_items").delete().eq("user_id", userId);

        // Insert current cart
        if (cart.length > 0) {
          const rows = cart
            .map((item) => {
              const uuid = PRODUCT_UUID_MAP[item.id];
              if (!uuid) return null;
              return {
                user_id: userId,
                product_id: uuid,
                quantity: item.qty,
                variant_id: item.variantId ?? null,
                bundle_id: item.bundleId ?? null,
              };
            })
            .filter(Boolean);

          if (rows.length > 0) {
            await supabase.from("cart_items").insert(rows as any);
          }
        }
      } finally {
        syncingRef.current = false;
      }
    }, 500);

    return () => clearTimeout(syncTimeout.current);
  }, [cart, userId]);

  const addToCart = useCallback((p: any, q = 1) => {
    setCart((prev) => {
      const vid = (p as any).variantId ?? null;
      const bid = (p as any).bundleId ?? null;
      const ex = prev.find(
        (i) => i.id === p.id
          && (i.variantId ?? null) === vid
          && (i.bundleId ?? null) === bid,
      );
      if (ex) {
        return prev.map((i) =>
          i.id === p.id
          && (i.variantId ?? null) === vid
          && (i.bundleId ?? null) === bid
            ? { ...i, qty: i.qty + q }
            : i,
        );
      }
      return [...prev, { ...p, qty: q, variantId: vid, bundleId: bid }];
    });
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("nm:add-to-cart"));
      }
    } catch { /* no-op */ }
  }, []);

  // Both helpers accept an optional variantId for variant-aware lines.
  // When omitted, they match the first line for the given product id
  // (backward-compatible with existing callers).
  const updateQty = useCallback((id: number, qty: number, variantId?: string | null) => {
    setCart((prev) =>
      prev.map((i) => {
        if (i.id !== id) return i;
        if (variantId !== undefined && (i.variantId ?? null) !== variantId) return i;
        return { ...i, qty: Math.max(1, qty) };
      }),
    );
  }, []);

  const removeFromCart = useCallback((id: number, variantId?: string | null) => {
    setCart((prev) =>
      prev.filter((i) => {
        if (i.id !== id) return true;
        if (variantId !== undefined && (i.variantId ?? null) !== variantId) return true;
        return false;
      }),
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  return { cart, setCart, cartLoaded, addToCart, updateQty, removeFromCart, clearCart };
}
